import { useCallback, useMemo } from "react";
import { getCookie } from "../utils/Cookie";
import { tokenRefresh } from "../features/authentication/services/tokenRefresh";
import { authBus } from "../features/authentication/utils/authBus";
import ResponseError from "./ResponseError";
import { parseResponseBody } from "./parseResponseBody";
import { useNavigate } from "react-router-dom";
import {
  getCsrfToken,
  deleteCsrfToken,
  saveCsrfToken,
} from "../utils/storage/csrf";

/**
 * Global refresh gate: ensure only one refresh happens at a time.
 * All pending requests await the same promise.
 */
let refreshInFlight = null;

/** Default merge of headers; adds JSON + CSRF when appropriate */
function buildHeaders(userHeaders = {}, body) {
  const headers = new Headers(userHeaders || {});

  // If the body is a plain object, default to JSON
  const isPlainObjectBody =
    body &&
    typeof body === "object" &&
    !(body instanceof FormData) &&
    !(body instanceof Blob);

  if (isPlainObjectBody && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  // Always accept JSON by default unless caller overrides
  if (!headers.has("Accept")) headers.set("Accept", "application/json");

  // Add CSRF token from cookie if present and not already provided
  let csrf = getCookie("csrf_token");
  if (!csrf) csrf = getCsrfToken();

  if (csrf && !headers.has("X-CSRF-Token")) {
    headers.set("X-CSRF-Token", csrf);
  } else {
  }

  // for (const [name, value] of headers.entries()) {
  //   console.log(`${name}: ${value}`);
  // }

  return headers;
}

/** AbortController-based timeout wrapper */
async function withTimeout(promise, ms, controller) {
  if (!ms) return promise;
  const timeout = new Promise((_, reject) => {
    setTimeout(() => {
      controller?.abort?.();
      reject(new Error("Request timed out"));
    }, ms);
  });
  return Promise.race([promise, timeout]);
}

/**
 * isSafeToRetry based on safety to do so
 * @param {*} status
 * @param {*} method
 * @returns {boolean}   true | false
 *
 * @remarks
 * Should we retry this response/status?
 *
 * Idempotent-ish methods (safe to retry)
 *
 * The following HTTP methods are considered idempotent and are typically safe to retry by default:
 *   - GET: Retrieves a resource. Repeated requests don't change the server's state.
 *   - HEAD: Retrieves metadata for a resource. Same as GET, but without the response body.
 *   - OPTIONS: Retrieves a list of supported HTTP methods for a URL.
 *   - PUT: Replaces a resource at a specific URI. A repeated PUT request will simply replace the resource with the same data again.
 *   - DELETE: Deletes a resource. A repeated DELETE request for an already-deleted resource will not change the outcome.
 *
 * Non-idempotent methods (not safe to retry)
 *
 * These methods are not typically retried by default because re-running them could lead to unintended consequences:
 *   - POST: Creates a new resource. If a user's initial POST request fails and is automatically retried, it could create a duplicate resource, like charging a customer twice.
 *   - PATCH: Applies a partial modification to a resource. Its idempotency can vary depending on the specific implementation on the server, so it is generally considered unsafe to auto-retry.
 */
function isSafeToRetry(status, method) {
  // Only retry idempotent-ish methods by default
  const m = (method || "GET").toUpperCase();
  const idempotent = m === "GET" || m === "HEAD" || m === "OPTIONS";
  if (!idempotent) return false;

  // Retry for 429 and 5xx excluding 501
  if (status === 429) return true; // too many requests
  if (status >= 500 && status !== 501) return true; // server error

  return false;
}

/**
 * Attempt to refresh (deduped): all callers await the same promise.
 * Returns true if refresh succeeded, false otherwise.
 *
 * @returns {boolean}   true | false
 */
async function ensureRefreshed() {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        // returns {success,data}
        return await tokenRefresh();
      } finally {
        // Allow future refreshes after this chain resolves
        const ok = refreshInFlight;
        // Defer clearing so awaiting callers see resolved state
        setTimeout(() => {
          if (refreshInFlight === ok) refreshInFlight = null;
        }, 0);
      }
    })();
  }

  try {
    return await refreshInFlight;
  } catch {
    return { success: false, data: {} };
  }
}

// module-scope sleep helper
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * useAuthorizedFetch
 *
 * @param {object} defaults
 * @param {number} defaults.timeoutMs   default 20000
 * @param {number} defaults.maxRetries  default 2
 * @param {boolean} defaults.retryOnFail default true
 * @returns {(url: string|Request, options?: {
 *   timeoutMs?: number,
 *   maxRetries?: number,
 *   retryOnFail?: boolean,
 *   headers?: HeadersInit,
 *   body?: any,
 *   [key:string]: any
 * }) => Promise<Response|any>}  // note: returns logout result if refresh fails (preserved behavior)
 */
export function useAuthorizedFetch(defaults = {}) {
  const navigate = useNavigate();
  const mergedDefaults = useMemo(
    () => ({
      timeoutMs: 20000,
      maxRetries: 0,
      retryOnFail: true,
      ...defaults, // <- new defaults override the base every render
    }),
    [defaults] // make sure caller passes a stable object or memoize it
  );

  const fetchWithAuth = useCallback(
    async (url, options = {}) => {
      const {
        timeoutMs = mergedDefaults.timeoutMs,
        maxRetries = mergedDefaults.maxRetries,
        retryOnFail = mergedDefaults.retryOnFail,
        headers: userHeaders,
        body: rawBody,
        ...rest
      } = options;
      console.log(retryOnFail);
      /*
  // ✅ hooks called at top-level (not inside loops/conditions)
  // const logout = useUserLogout();

  // Keep stable default options between renders
  const defaultsRef = useRef({
    timeoutMs: 20000,
    maxRetries: 2,
    retryOnFail: true,
    ...defaults,
  });
  console.log("defaults: ");
  console.log(defaultsRef.current);
  const fetchWithAuth = useCallback(async (url, options = {}) => {
    const {
      timeoutMs = defaultsRef.current.timeoutMs,
      maxRetries = defaultsRef.current.maxRetries,
      retryOnFail = defaultsRef.current.retryOnFail,
      headers: userHeaders,
      body: rawBody,
      ...rest
    } = options;
    // console.log("userHeaders: ");
    // console.log(" userHeaders as Array:", [userHeaders]);
    // console.dir(userHeaders);
*/
      // Normalize/encode body if needed
      const headers = buildHeaders(userHeaders, rawBody);
      const body =
        rawBody &&
        typeof rawBody === "object" &&
        !(rawBody instanceof FormData) &&
        !(rawBody instanceof Blob)
          ? JSON.stringify(rawBody)
          : rawBody ?? undefined;

      // Final fetch options
      const baseOptions = {
        credentials: "include", // always send cookies
        redirect: "follow",
        headers,
        body,
        ...rest,
      };
      // console.log("baseOptions: ");
      // console.dir(baseOptions);
      const method = (baseOptions.method || "GET").toUpperCase();

      /**
       * Exponential backoff: 150ms * 2^attempt + jitter
       *
       * @param {*} attempt max number of attempts to make
       * @returns {integer} random integer value representing a delay in milliseconds
       *
       * @remarks
       * Exponential Backoff: 150 * Math.pow(2, attempt)
       *   - This part calculates an exponentially increasing delay.
       *   - 150 is a base delay in milliseconds.
       *   - Math.pow(2, attempt) raises 2 to the power of the attempt number.
       *     This means the delay will double with each subsequent attempt
       *     (e.g., for attempt = 0, it's 150ms; for attempt = 1, it's 300ms; for attempt = 2, it's 600ms, and so on).
       *
       * Jitter: + Math.floor(Math.random() * 150)
       *   - This part adds a random "jitter" to the calculated delay.
       *   - Math.random() generates a floating-point number between 0 (inclusive) and 1 (exclusive).
       *   - Multiplying by 150 scales this random number to be between 0 and 149.99...
       *   - Math.floor() rounds this down to the nearest whole number, resulting in a random integer between 0 and 149.
       *   - Adding this random value to the exponential backoff helps to prevent multiple clients or processes from
       *     retrying at precisely the same time, which could lead to further congestion or resource contention.
       *
       * In summary: The backoff function is designed to calculate a delay time for retrying an operation
       * (like a failed network request). This delay grows exponentially with each retry attempt and
       * includes a random component to distribute retries over time, making the system more resilient
       * and less likely to overwhelm a struggling service.
       *
       */
      const backoff = (attempt) =>
        150 * Math.pow(2, attempt) + Math.floor(Math.random() * 150);

      let attempt = 0; // attempt counter
      let lastError = null;
      let attemptedRetry = false; //set attempted retry to false

      while (attempt <= maxRetries) {
        const controller = new AbortController();
        const opts = { ...baseOptions, signal: controller.signal };

        try {
          console.log("fetching original URL");
          // First request attempt of original Request/URL
          // (will allways make 1 attempt even if maxRetries = 0, because if attempt = 0, 0 <= 0)
          let response = await withTimeout(
            fetch(url, opts),
            timeoutMs,
            controller
          );

          /**
           * Handle original request 401/403 (unauthorized/forbidden)
           */
          if (
            (response.status === 401 || response.status === 403) &&
            retryOnFail &&
            !attemptedRetry
          ) {
            console.log("original URL failed with " + response.status);

            // original request (requiring authorization) failed set local auth status
            //   try a single refresh path (deduped across requests)

            // at this point we need to set user as "unauthenticated" as a safety net
            authBus.emit({ type: "unauthenticated" });

            // Attempt to refresh tokens
            const { success, data } = await ensureRefreshed(); //returns json success: true/false

            /**  data =
             * {
             *     "success": true,
             *     "message": "Refresh Succeeded.",
             *     "data": {
             *         "csrf_token": "5e5c6c1fa8d865383353dfb552f5f812"
             *     }
             * }
             */
            if (success) {
              console.log("token refresh succeeded");

              // auth tokens refresh successful, we are authorized
              authBus.emit({ type: "authenticated" });

              // clean up old csrf from local
              // save new csrf to local
              deleteCsrfToken();
              saveCsrfToken(data?.data?.csrf_token);

              // mark that we have attempted retrying the original request
              attemptedRetry = true;

              //update original request with new csrf token
              opts.headers.set("X-CSRF-Token", data?.data?.csrf_token);

              // retry original request with the *same* options (cookies now hold fresh tokens)
              console.log("RE-fetching original URL");

              response = await withTimeout(
                fetch(url, opts),
                timeoutMs,
                controller
              );
            } else {
              // Refresh failed: force logout
              // (preserves existing behavior of returning logout's response)
              // const logoutResult = await logout();
              // return logoutResult;
              authBus.emit({ type: "unauthenticated" }); // refresh failed
              console.log("login1");
              navigate("/login");
              return response;
            }
          }

          // still unauthorized after refresh attempt

          if (
            !response.ok &&
            (response.status === 401 || response.status === 403)
          ) {
            console.log(
              "second fetch of original URL failed with " + response.status
            );

            authBus.emit({ type: "unauthenticated" }); // refresh failed, unauthorized
            // [ ] verify all local tokens are cleaned up
            navigate("/login");

            // get response info
            const data = await parseResponseBody(response);

            const out = {
              success: response.ok,
              status: response.status,
              statusText: response.statusText,
              url: response.url,
              headers: Object.fromEntries(response.headers.entries()),
              data: data?.data ?? data ?? {},
              meta: data?.meta ?? {},
              // text: raw,
            };

            // if (!response.ok) {
            //   // Server returned an error status — throw with rich context
            throw new ResponseError(
              data?.message || response.statusText || "Request failed",
              out
            );
            // }

            // return out; //returning response from original request (not request for refresh)
          }

          // Retry safe temporary or intermittent errors
          // only if we have not exhausted configured max retry attempts
          //  - configures delay, increases attempt counter and continues
          if (isSafeToRetry(response.status, method) && attempt < maxRetries) {
            console.log("idempotent request, safe to retry");

            const delayMs = backoff(attempt);
            await sleep(delayMs);
            attempt++;
            continue;
          }

          // Success or non-retryable status
          // If a protected request succeeded, we are authenticated
          if (response.ok) {
            console.log("fetch of original UR succeeded.");
            authBus.emit({ type: "authenticated" });
          } else {
            //DEBUG
            console.log("login3");
            navigate("/login");
          }

          //process original request response from server
          const data = await parseResponseBody(response);

          // format response with data
          const out = {
            success: response.ok,
            status: response.status,
            statusText: response.statusText,
            url: response.url,
            headers: Object.fromEntries(response.headers.entries()),
            data: data?.data ?? data ?? {},
            meta: data?.meta ?? {},
          };

          return out;
        } catch (err) {
          // Network/timeout: retry if allowed and idempotent
          lastError = err;
          const isAbort =
            err?.name === "AbortError" ||
            /timed out/i.test(String(err?.message));
          const canRetry =
            attempt < maxRetries &&
            (method === "GET" || method === "HEAD" || method === "OPTIONS");
          if (canRetry) {
            const delayMs = backoff(attempt);
            await sleep(delayMs);
            attempt++;
            continue;
          }
          // No more retries
          throw isAbort ? new Error("Network timeout or abort") : err;
        }
      }

      // Should rarely reach here; surface the last network error if any
      if (lastError) throw lastError;
      // Fallback: conservative error
      throw new Error("Authorized Fetch: exhausted retries");
    },
    [mergedDefaults, navigate]
  );

  return fetchWithAuth;
}

export default useAuthorizedFetch;

// Pre Hook code below

// // import { refreshToken, forceLogout } from "./auth.js";

// import { useUserLogout } from "./UserAuth";
// import { getCookie } from "../../utils/Cookie";
// import { tokenRefresh } from "./Token";

// /**
//  * Global refresh gate: ensure only one refresh happens at a time.
//  * All pending requests await the same promise.
//  */
// let refreshInFlight = null;

// /** Default merge of headers; adds JSON + CSRF when appropriate */
// function buildHeaders(userHeaders = {}, body) {
//   const headers = new Headers(userHeaders || {});
//   // If the body is a plain object, default to JSON
//   if (
//     body &&
//     typeof body === "object" &&
//     !(body instanceof FormData) &&
//     !(body instanceof Blob)
//   ) {
//     if (!headers.has("Content-Type"))
//       headers.set("Content-Type", "application/json");
//   }
//   // Always accept JSON by default unless caller overrides
//   if (!headers.has("Accept")) headers.set("Accept", "application/json");

//   // Add CSRF token from cookie if present and not already provided
//   const csrf = getCookie("csrf_token");
//   if (csrf && !headers.has("X-CSRF-Token")) headers.set("X-CSRF-Token", csrf);

//   return headers;
// }

// /** AbortController-based timeout wrapper */
// async function withTimeout(promise, ms, controller) {
//   if (!ms) return promise;
//   const timeout = new Promise((_, reject) => {
//     setTimeout(() => {
//       controller?.abort?.();
//       reject(new Error("Request timed out"));
//     }, ms);
//   });
//   return Promise.race([promise, timeout]);
// }

// /** Should we retry this response/status? */
// function isSafeToRetry(status, method) {
//   // Only retry idempotent-ish methods by default
//   const m = (method || "GET").toUpperCase();
//   const idempotent = m === "GET" || m === "HEAD" || m === "OPTIONS";
//   if (!idempotent) return false;

//   // Retry for 429 and 5xx excluding 501
//   if (status === 429) return true; //too many requests
//   if (status >= 500 && status !== 501) return true; //server error

//   return false;
// }

// /**
//  * Attempt to refresh (deduped): all callers await the same promise.
//  * Returns true if refresh succeeded, false otherwise.
//  */
// async function ensureRefreshed() {
//   if (!refreshInFlight) {
//     refreshInFlight = (async () => {
//       try {
//         return await tokenRefresh(); // your impl should return true/false
//       } finally {
//         // Allow future refreshes after this chain resolves
//         const ok = refreshInFlight;
//         // Defer clearing to next tick so awaiting callers see resolved state
//         setTimeout(() => {
//           if (refreshInFlight === ok) refreshInFlight = null;
//         }, 0);
//       }
//     })();
//   }

//   try {
//     return await refreshInFlight;
//   } catch {
//     return false;
//   }
// }

// // put this at module scope (outside authFetch)
// const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// /**
//  * Authenticated fetch
//  *
//  * @param {string|Request} url
//  * @param {object} options
//  * @param {number} options.timeoutMs         Abort if exceeded (default 20000)
//  * @param {number} options.maxRetries        Retries for 5xx/429/network (default 2)
//  * @param {boolean} options.retryOnFail      Whether to retry once after refresh (default true)
//  * @param {HeadersInit} options.headers
//  * @param {any} options.body                 If object, auto-JSON stringified
//  * @returns {Promise<Response>}
//  */
// export async function fetchWithAuth(url, options = {}) {
//   const {
//     timeoutMs = 20000,
//     maxRetries = 2,
//     retryOnFail = true,
//     headers: userHeaders,
//     body: rawBody,
//     ...rest
//   } = options;

//   // Normalize/encode body if needed
//   const headers = buildHeaders(userHeaders, rawBody);
//   const body =
//     rawBody &&
//     typeof rawBody === "object" &&
//     !(rawBody instanceof FormData) &&
//     !(rawBody instanceof Blob)
//       ? JSON.stringify(rawBody)
//       : rawBody ?? undefined;

//   // Final fetch options
//   const baseOptions = {
//     credentials: "include", // always send cookies
//     redirect: "follow",
//     headers,
//     body,
//     ...rest,
//   };

//   const method = (baseOptions.method || "GET").toUpperCase();

//   // Exponential backoff: 150ms * 2^attempt + jitter
//   const backoff = (attempt) =>
//     150 * Math.pow(2, attempt) + Math.floor(Math.random() * 150);

//   let attempt = 0;
//   let lastError = null;
//   let attemptedRetry = false;

//   while (attempt <= maxRetries) {
//     const controller = new AbortController();
//     const opts = { ...baseOptions, signal: controller.signal };

//     try {
//       let response = await withTimeout(fetch(url, opts), timeoutMs, controller);

//       // Handle 401/403: try a single refresh path (deduped across requests)
//       if (
//         (response.status === 401 || response.status === 403) &&
//         retryOnFail &&
//         !attemptedRetry
//       ) {
//         const refreshed = await ensureRefreshed();
//         if (refreshed) {
//           attemptedRetry = true;
//           // retry once with the *same* options (cookies now hold fresh token)
//           response = await withTimeout(fetch(url, opts), timeoutMs, controller);
//         } else {
//           // Refresh failed: force logout and return original response
//           // ✅ Call hook at top level (once per render)
//           const logout = useUserLogout();

//           //  returns the server JSON (e.g., { success, message, data })
//           const response = await logout();

//           return response;
//         }
//       }

//       // If still unauthorized after refresh attempt, let the caller handle it
//       if (response.status === 401 || response.status === 403) {
//         return response;
//       }

//       // Retry transient errors
//       if (isSafeToRetry(response.status, method) && attempt < maxRetries) {
//         const delayMs = backoff(attempt); // capture current value
//         await sleep(delayMs); // no closure over `attempt`
//         attempt++;
//         continue;
//       }

//       // Success or non-retryable status
//       return response;
//     } catch (err) {
//       // Network/timeout: retry if allowed and idempotent
//       lastError = err;
//       const isAbort =
//         err?.name === "AbortError" || /timed out/i.test(String(err?.message));
//       const canRetry =
//         attempt < maxRetries &&
//         (method === "GET" || method === "HEAD" || method === "OPTIONS");
//       if (canRetry) {
//         const delayMs = backoff(attempt);
//         await sleep(delayMs);
//         attempt++;
//         continue;
//       }
//       // No more retries
//       throw isAbort ? new Error("Network timeout or abort") : err;
//     }
//   }

//   // Should rarely reach here; surface the last network error if any
//   if (lastError) throw lastError;
//   // Fallback: conservative error
//   throw new Error("fetchWithAuth: exhausted retries");
// }
